/**
 * Thin fetch wrappers for the unauthenticated passkey routes.
 *
 * These don't use @re-platform/api-client because that client always attaches
 * an Auth0 bearer token — these two routes are explicitly anonymous
 * (UNAUTHENTICATED_ROUTES in ApiStack). One less moving part.
 *
 * The base URL is read from `VITE_API_BASE_URL` (set in apps/web/.env.local
 * for local dev → http://localhost:8000; CI overrides per env).
 *
 * On tenant subdomains (e.g. mybrokerage.newbrainllc.us) we use
 * window.location.origin instead of the absolute API URL. The Cloudflare
 * subdomain-proxy Worker intercepts those calls and proxies them to
 * api.newbrainllc.us — keeping the fetch same-origin so there is no CORS
 * preflight or Origin-header check to worry about.
 */

const _platformDomain =
  (import.meta.env.VITE_PLATFORM_DOMAIN as string | undefined) ??
  "newbrainllc.us";
const _reserved = new Set(["app", "admin", "api", "www"]);

function _onTenantSubdomain(): boolean {
  const h = window.location.hostname;
  if (!h.endsWith(`.${_platformDomain}`)) return false;
  const sub = h.slice(0, -(_platformDomain.length + 1));
  return Boolean(sub) && !_reserved.has(sub) && !sub.includes(".");
}

const API_BASE_URL = _onTenantSubdomain()
  ? window.location.origin
  : ((import.meta.env.VITE_API_BASE_URL as string | undefined) ??
    "http://localhost:8000");

/**
 * A link the identity service hands out, made clickable (spec 024).
 *
 * A value starting with `/` is a path on the API — the stable compliance-doc
 * links (`/identity/tenant/by-slug/<slug>/compliance/IABS`), which answer each
 * click with a fresh presigned GET — so it goes on the API's origin: the
 * page's own on a brokerage subdomain (the Worker forwards /identity/), else
 * VITE_API_BASE_URL. Anything else (an absolute URL, such as the official TREC
 * CPN fallback) is used as it is.
 */
export function resolveApiHref(value: string): string;
export function resolveApiHref(value: string | null | undefined): string | null;
export function resolveApiHref(
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  return value.startsWith("/") ? `${API_BASE_URL}${value}` : value;
}

export interface TenantChoice {
  tenant_id: string;
  tenant_name: string;
}

/**
 * Thrown when an email belongs to a client in more than one brokerage (Finding
 * 3). The caller shows a brokerage picker and retries the begin call with the
 * chosen `tenant_id`.
 */
export class TenantAmbiguousError extends Error {
  readonly candidates: TenantChoice[];
  constructor(candidates: TenantChoice[]) {
    super("This email belongs to more than one brokerage.");
    this.name = "TenantAmbiguousError";
    this.candidates = candidates;
  }
}

/**
 * 🔴 NO EMAIL. The address comes off the signup token, which exists only because the
 * client clicked a link we emailed to that inbox (V057).
 *
 * This route used to take an email and hand a WebAuthn challenge to whoever it named —
 * so anyone who knew a client's address could bind their own Face ID to that client's
 * account. There is now nothing here for an attacker to supply.
 */
export interface RegisterBeginRequest {
  signup_token: string;
}

/** Step ONE of signup: ask for the link. No account is created. */
export interface SignupRequestBody {
  email: string;
  display_name?: string;
  /**
   * From an agent's referral link (`/join?ref=<token>`). Resolved server-side to
   * a (tenant, agent); takes precedence over the domain. A referral, forwardable,
   * so it only decides which brokerage/agent — never whether an account is made.
   */
  invite_token?: string;
  /**
   * NO LONGER AUTHORITATIVE — the tenant is resolved from the request Origin
   * server-side (Phase 13). Retained only as a local-dev fallback.
   */
  tenant_id?: string;
  /** SEC-12 — see deviceSignal.ts. Voluntary; a flag for admin review, never a gate. */
  device_signal?: string;
}

export interface SignupRequestResponse {
  /** Always the same, whatever the address. Never branch on it. */
  message: string;
}

export interface RegisterBeginResponse {
  challenge_id: string;
  /** The address the token proved — the browser never sent one. */
  email: string;
  // PublicKeyCredentialCreationOptionsJSON — opaque to us; passed straight to
  // `@simplewebauthn/browser.startRegistration({optionsJSON: publicKey})`.
  public_key: Record<string, unknown>;
}

export interface RegisterFinishRequest {
  challenge_id: string;
  attestation: Record<string, unknown>;
  /** The same token /begin was given — burnt here, once the account exists. */
  signup_token: string;
}

export interface RegisterFinishResponse {
  credential_id: string;
  user_id: string;
  tenant_id: string;
  /** Agent this signup was attributed to server-side, from the invite token or
   *  the domain. Present even when the agent's link was opened on another
   *  device, which is the case localStorage alone cannot cover. */
  agent_id: string | null;
}

export interface LoginBeginRequest {
  email: string;
  tenant_id?: string;
}

export interface LoginBeginResponse {
  challenge_id: string;
  // PublicKeyCredentialRequestOptionsJSON — passed straight to
  // `@simplewebauthn/browser.startAuthentication({optionsJSON: publicKey})`.
  public_key: Record<string, unknown>;
}

export interface LoginFinishRequest {
  challenge_id: string;
  assertion: Record<string, unknown>;
}

export interface LoginFinishResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  /** Opaque 7-day token. The backend has always returned it; this app used to
   *  drop it on the floor, which is why every session died after 15 minutes. */
  refresh_token: string;
  user_id: string;
  tenant_id: string;
  primary_role: string;
  email: string;
  display_name: string | null;
}

async function postJson<TReq, TRes>(path: string, body: TReq): Promise<TRes> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    // `detail` is a string for ordinary errors, but a structured object for the
    // tenant-ambiguous 409 (Finding 3): { reason, message, candidates }.
    let detail: unknown;
    try {
      detail = ((await res.json()) as { detail?: unknown }).detail;
    } catch {
      // fall through with status only
    }
    if (
      res.status === 409 &&
      detail !== null &&
      typeof detail === "object" &&
      (detail as { reason?: string }).reason === "tenant_ambiguous"
    ) {
      const candidates =
        (detail as { candidates?: TenantChoice[] }).candidates ?? [];
      throw new TenantAmbiguousError(candidates);
    }
    const message =
      typeof detail === "string"
        ? detail
        : (detail as { message?: string } | undefined)?.message;
    throw new Error(message ?? `Request failed: ${res.status}`);
  }
  return (await res.json()) as TRes;
}

// ---------------------------------------------------------------------------
// Public identity-svc helpers (tenant lookup + client-invite landing)
// ---------------------------------------------------------------------------

export interface BrokeragePublic {
  legal_name: string;
  trec_license_number: string | null;
  nmls_id: string | null;
  primary_phone: string | null;
  address_street: string | null;
  address_city: string | null;
  address_state: string | null;
  address_zip: string | null;
  /** Short-lived presigned GET — re-fetched per page load, never a fixed URL. */
  logo_url: string | null;
  primary_color: string | null;
  secondary_color: string | null;
  /** Presigned GET for the brokerage's own IABS, or null until one is on file. */
  iabs_url: string | null;
  /** Always present — the brokerage's upload if any, else the official TREC link. */
  cpn_url: string;
}

export interface TenantLookupResponse {
  tenant_id: string;
  slug: string;
  display_name: string;
  /** REAL_ESTATE | MORTGAGE — which compliance notices the landing page shows. */
  brokerage_type: string;
  rp_id: string | null;
  /** Set when the domain is an agent's own — the client is theirs (V060). */
  agent_id: string | null;
  agent_name: string | null;
  /** Broker identification for the public landing page (TREC/NMLS compliance). */
  brokerage: BrokeragePublic | null;
}

export interface AgentPublic {
  agent_id: string;
  /** users.id — same identifier V060 carries into onboarding. */
  user_id: string;
  full_name: string;
  /** Always the real, dialable E.164 number — use for a tel: href. */
  phone: string | null;
  /** What to SHOW (may be a vanity spelling, V070) — never use for tel:. */
  phone_display: string | null;
  email: string | null;
  trec_license_number: string | null;
  nmls_id: string | null;
  /** Presigned GET for the agent's OWN IABS upload, or null until one is on file. */
  iabs_url: string | null;
  /** Presigned GET for the agent's own headshot, or null until one is uploaded. */
  photo_url: string | null;
  /** True when this same human also holds an agents row in the OTHER discipline. */
  dual_licensed: boolean;
  secondary_brokerage_type: string | null;
  secondary_brokerage_name: string | null;
  secondary_license_number: string | null;
  /** The OTHER tenant's full public brokerage identity — same shape as
   * `tenant.brokerage` for this page's own side. Set only when dual_licensed. */
  secondary_brokerage: BrokeragePublic | null;
}

export interface AgentLandingResponse {
  tenant: TenantLookupResponse;
  agent: AgentPublic;
}

export interface ClientInviteResponse {
  token: string;
  tenant_id: string;
  /** users.id — NOT the agents.id the /b/:slug/a/:agentId route expects. */
  agent_id: string;
  /** agents.id — use this (with slug) to redirect into the agent-landing page. */
  agent_profile_id: string | null;
  slug: string;
  agent_name: string;
  agent_phone: string | null;
  tenant_name: string;
  expires_at: string;
}

async function getJson<TRes>(path: string): Promise<TRes> {
  const res = await fetch(`${API_BASE_URL}${path}`);
  if (!res.ok) {
    let detail: unknown;
    try {
      detail = ((await res.json()) as { detail?: unknown }).detail;
    } catch {
      // fall through
    }
    const message =
      typeof detail === "string" ? detail : `Request failed: ${res.status}`;
    throw new Error(message);
  }
  return (await res.json()) as TRes;
}

export interface IntakeBody {
  name: string;
  email: string;
  phone?: string;
  message?: string;
}

export const identityApi = {
  getTenantBySlug(slug: string) {
    return getJson<TenantLookupResponse>(
      `/identity/tenant/by-slug/${encodeURIComponent(slug)}`,
    );
  },
  getTenantByDomain(domain: string) {
    return getJson<TenantLookupResponse>(
      `/identity/tenant/by-domain?domain=${encodeURIComponent(domain)}`,
    );
  },
  getAgentLanding(slug: string, agentId: string) {
    return getJson<AgentLandingResponse>(
      `/identity/tenant/by-slug/${encodeURIComponent(slug)}/agent/${encodeURIComponent(agentId)}`,
    );
  },
  getInvite(token: string) {
    return getJson<ClientInviteResponse>(
      `/identity/client-invites/${encodeURIComponent(token)}`,
    );
  },
  /**
   * When a signup resolves to no brokerage, we never invent a tenant — the
   * visitor leaves their details and support reaches back. No account is created.
   */
  submitIntake(body: IntakeBody) {
    return postJson<IntakeBody, { message: string }>("/identity/intake", body);
  },
};

export const passkeyApi = {
  /**
   * Ask us to email a signup link. The response is IDENTICAL whether the address is
   * new, already registered, or one we refuse — do not try to read anything into it.
   * The branching happens in the client's inbox, not here.
   */
  requestSignup(req: SignupRequestBody) {
    return postJson<SignupRequestBody, SignupRequestResponse>(
      "/identity/passkey/signup/request",
      req,
    );
  },

  registerBegin(req: RegisterBeginRequest) {
    return postJson<RegisterBeginRequest, RegisterBeginResponse>(
      "/identity/passkey/register/begin",
      req,
    );
  },
  registerFinish(req: RegisterFinishRequest) {
    return postJson<RegisterFinishRequest, RegisterFinishResponse>(
      "/identity/passkey/register/finish",
      req,
    );
  },
  loginBegin(req: LoginBeginRequest) {
    return postJson<LoginBeginRequest, LoginBeginResponse>(
      "/identity/passkey/login/begin",
      req,
    );
  },
  loginFinish(req: LoginFinishRequest) {
    return postJson<LoginFinishRequest, LoginFinishResponse>(
      "/identity/passkey/login/finish",
      req,
    );
  },

  // -- device link: a passkey on a SECOND device (V072) ---------------------
  //
  // Registration is reachable only via a signup token, and that token is
  // refused once the account has a credential — so this is the ONLY way to add
  // a second device. Without it, a lost phone is a lost account.
  //
  // Only the ANONYMOUS half lives here: postJson sends no Authorization header
  // by design. The signed-in half (mint / list / delete) is in
  // @re-platform/api-client's passkeyService, which carries the session.

  /**
   * "No passkey on this device?" — emails a link to the address on the account.
   * The response is IDENTICAL whether or not the account exists; the only
   * branch is a multi-brokerage email, which throws TenantAmbiguousError just
   * like login does.
   */
  requestDeviceLink(req: { email: string; tenant_id?: string }) {
    return postJson<typeof req, { message: string }>(
      "/identity/passkey/device-link/request",
      req,
    );
  },

  deviceLinkBegin(req: { device_link_token: string }) {
    return postJson<typeof req, RegisterBeginResponse>(
      "/identity/passkey/device-link/register/begin",
      req,
    );
  },

  /** Persists the new credential AND signs this device in (returns a session). */
  deviceLinkFinish(req: {
    challenge_id: string;
    attestation: Record<string, unknown>;
    device_link_token: string;
    friendly_name?: string;
  }) {
    return postJson<typeof req, LoginFinishResponse>(
      "/identity/passkey/device-link/register/finish",
      req,
    );
  },
};
